// Pointer Events for the board: drag a run (6 px threshold, pointer capture), tap/click on the
// stock, tap = send to foundation on touch, click on a face-down top card = flip, double-click or
// right-click = send to foundation. Game state never changes here directly: every action goes
// through the controller.
import { useEffect, type RefObject } from 'react';
import type { GameController } from '../engine/controller';
import type { PileRef } from '../engine/types';
import { collectCandidates, landingRect, parsePile, pickDropTarget } from './dropTarget';
import { dragBack } from './dragBack';
import { endGlide, markRunHead, startGlide } from '../board/glide';
import { motion } from '../engine/motion';
import { RULES } from '../engine/rules';

export const DRAG_THRESHOLD_PX = 6;
/** Two mouse clicks on the same card within this time = a double-click (send to a foundation). */
export const DOUBLE_CLICK_MS = 400;
export const JUST_DRAGGED_MS = 100;
/** A press that moved up to this far is a click (mouse) or a tap (touch), never a drop, even when the slipping
 *  card touched a neighbour (player report 2026-10-23, "mouse clicks are now ignored when cursor is in
 *  motion"; the owner: taps had to be very still, and older players' hands shake). Legacy had none of this:
 *  it took the stock and a face-down card's flip on the browser's click, and a card drag from 6 px. A real
 *  drop is always further (the next column is a card's width away); the run still follows the pointer from
 *  DRAG_THRESHOLD_PX. Never more than half the card's width (small cards: the next column is closer). */
export const CLICK_SLOP_PX = 24;
export const TAP_SLOP_PX = 32;
/** Touches that start this close to the screen's side edge are kept from Safari's Back / Forward swipe. */
export const EDGE_SWIPE_PX = 28;
/** Mouse moves in a row with no button held that end a press whose release never came (ENG-093). One is not enough:
 *  a Mac trackpad in Chrome / Opera often sends one such move just before the release (report 1407, the owner's
 *  trackpad 2026-10-26: 1 drag in 4), and the card went back. */
export const LOST_RELEASE_MOVES = 3;

interface Press {
  pointerId: number;
  pointerType: string;
  startX: number;
  startY: number;
  cardEl: HTMLElement | null;
  pile: PileRef | null;
  pileName: string;
  index: number;
  liftable: boolean;
  dragging: boolean;
  /** The pointer went past the drag threshold (a non-liftable card: no drag, but no click either). */
  moved: boolean;
  /** The browser said which buttons were down at the press (Safari 14 always says none, see onPointerMove). */
  saysButtons: boolean;
  /** Mouse moves in a row that said no button was held (see LOST_RELEASE_MOVES). */
  noButtonMoves: number;
  runEls: HTMLElement[];
}

export function usePointerInput(boardRef: RefObject<HTMLDivElement | null>, ctrl: GameController) {
  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    let press: Press | null = null;
    let lastClick: { cardEl: HTMLElement; at: number } | null = null;
    let lastDragEndAt = 0;
    // Pyramid: cards pair by a drop or by two clicks / taps; a game of pairs also drags the stock's face-up top card
    // and drops on the waste's and the stock's top cards
    const pairsGame = () => RULES[ctrl.getState().game].pairs === true;
    /** Pyramid: the card picked by a click or tap, waiting for its partner (board.css data-selected). */
    let selected: { el: HTMLElement; pile: PileRef; index: number } | null = null;
    const clearSelected = () => {
      selected?.el.removeAttribute('data-selected');
      selected = null;
    };
    // a change of the cards (a move, an undo, a new deal) drops the pick; the clock's tick does not (owner 2026-10-24:
    // the frame went off at the next full second, sometimes before the second tap, which then only picked its card)
    let seenPiles = ctrl.getState().piles;
    const unsubscribe = ctrl.subscribe((st) => {
      if (st.piles === seenPiles) return;
      seenPiles = st.piles;
      clearSelected();
    });
    /** Pyramid's click or tap: a second card that makes 13 with the picked one pairs them; a King leaves at once;
     *  another card in play is picked (again: dropped); the stock turns its card. */
    const pairClick = (p: Press) => {
      const pick = selected;
      const target = p.pile;
      if (pick && target && p.cardEl !== pick.el && ctrl.canDrop({ pile: pick.pile, index: pick.index }, target)) {
        clearSelected();
        ctrl.moveRun({ pile: pick.pile, index: pick.index }, target);
        return;
      }
      if (pick && p.cardEl === pick.el) {
        clearSelected();
        return;
      }
      clearSelected();
      if (target?.kind === 'stock') {
        ctrl.clickStock();
        return;
      }
      if (!p.cardEl || !target || !p.liftable) return;
      if (ctrl.sendToFoundation({ pile: target, index: p.index }) === 'moved') return;
      selected = { el: p.cardEl, pile: target, index: p.index };
      p.cardEl.setAttribute('data-selected', '');
    };

    const cardAt = (target: EventTarget | null): HTMLElement | null => (target instanceof Element ? target.closest<HTMLElement>('.card[data-pile]') : null);
    /** A card still gliding ignores the pointer (board.css data-moving: a fast second stock click must reach the stock,
     *  KL-B01), so a quick press where it lands took the card under it, or nothing (player report 2026-10-25, "very
     *  sluggish, you have to slowly put the card in place": 300 ms after every move in the classic mode). The press
     *  takes the highest gliding card that lands under the pointer, on the pile the pointer is over (or anywhere when
     *  it is over nothing); its glide ends at once. */
    const landingCardAt = (x: number, y: number, under: HTMLElement | null, slot: HTMLElement | null): HTMLElement | null => {
      const overPile = under?.dataset.pile ?? slot?.dataset.pile ?? null;
      let best: HTMLElement | null = null;
      for (const el of board.querySelectorAll<HTMLElement>('.card[data-moving]:not([data-dragging])')) {
        const pile = el.dataset.pile;
        if (!pile || pile === 'deck' || pile === 'stock') continue;
        if (overPile !== null && pile !== overPile) continue;
        if (under && Number(el.dataset.index) <= Number(under.dataset.index)) continue;
        const r = landingRect(el);
        if (x < r.left || x > r.right || y < r.top || y > r.bottom) continue;
        if (!best || Number(el.dataset.index) > Number(best.dataset.index)) best = el;
      }
      return best;
    };
    const slotAt = (target: EventTarget | null): HTMLElement | null => (target instanceof Element ? target.closest<HTMLElement>('.blank[data-pile]') : null);

    /** `glide: false` puts a short slip back at once: a gliding card ignores the pointer (board.css
     *  data-moving), so the second click of a double-click would hit the card under it. */
    const clearDrag = (glide = true, snap = false) => {
      if (!press) return;
      const classic = motion().classic;
      press.runEls.forEach((el, i) => {
        // classic (engine/motion.ts, legacy game.js checkMove / card.js returnPosition): a run dropped on a legal
        // place is there at once; one sent back glides 300 ms, each card 25 ms after the one under it
        if (classic && snap) {
          el.dataset.snap = '';
          requestAnimationFrame(() => requestAnimationFrame(() => delete el.dataset.snap));
        } else if (classic) el.style.setProperty('--classic-delay', `${i * 25}ms`);
        el.removeAttribute('data-dragging');
        el.style.removeProperty('--drag-dx');
        el.style.removeProperty('--drag-dy');
        // the run glides from the pointer to its pile (back or onto the target) still above the table:
        // data-moving keeps it on top and keeps its shadow (board.css) until it lands
        if (press!.dragging && glide && !(classic && snap)) startGlide(el, { released: true });
      });
    };

    // The pointers that are down, with their kind. A pointer is forgotten at its pointerup / pointercancel, but one of
    // those may never reach the board (player reports 2026-10-25, Firefox on a Windows touch screen: "I can't move the
    // cards, it seems blocked"; Chrome on an iPad: "the game freezes up all the time"): the pointer stayed here, every
    // later press looked like a second finger and was ignored, until a reload. So the first pointer of a kind (isPrimary)
    // forgets the others of its kind, and only pointers of one kind count as two fingers.
    const activePointers = new Map<number, string>();
    const onPointerDown = (e: PointerEvent) => {
      if (e.isPrimary) for (const [id, type] of activePointers) if (type === e.pointerType) activePointers.delete(id);
      activePointers.set(e.pointerId, e.pointerType);
      if ([...activePointers.values()].filter((type) => type === e.pointerType).length > 1) {
        // Two fingers down: no gesture (legacy ignored multi-touch); a started press is dropped.
        if (press) {
          clearDrag();
          press = null;
        }
        return;
      }
      // a press still open here lost its end (see activePointers): it is dropped, its run goes back, and this one counts.
      // (Safari 14, Playwright 1.3's WebKit, also drops the board's capture after the first move of a drag, so a lost
      // capture does not tell that a press is over.)
      if (press) {
        dragBack(press.dragging ? 'drag back: its release never came' : 'press ended: its release never came');
        clearDrag();
        press = null;
      }
      if (e.button !== 0) return;
      let cardEl = cardAt(e.target);
      const slotEl = slotAt(e.target);
      const landing = landingCardAt(e.clientX, e.clientY, cardEl, slotEl);
      if (landing) {
        endGlide(landing);
        cardEl = landing;
      }
      const pileName = cardEl?.dataset.pile ?? slotEl?.dataset.pile ?? '';
      const pile = parsePile(pileName);
      const index = cardEl ? Number(cardEl.dataset.index) : -1;
      const liftable = !!cardEl && !!pile && (pile.kind !== 'stock' || pairsGame()) && ctrl.canLift(pile, index);
      press = { pointerId: e.pointerId, pointerType: e.pointerType, startX: e.clientX, startY: e.clientY, cardEl, pile, pileName, index, liftable, dragging: false, moved: false, saysButtons: e.buttons !== 0, noButtonMoves: 0, runEls: [] };
      if (liftable) {
        // in pile order, the pressed card first: the drop is judged by runEls[0]'s box. The DOM keeps the cards in
        // deal order, not pile order, so unsorted a long run was judged by a card further down, which missed the
        // target and the run went back (player reports 2026-10-23: Spider "9..A does not go onto the 10")
        press.runEls = [...board.querySelectorAll<HTMLElement>(`.card[data-pile="${pileName}"]`)]
          .filter((el) => Number(el.dataset.index) >= index)
          .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
      }
      try {
        board.setPointerCapture(e.pointerId);
      } catch {
        /* capture is best effort */
      }
      // once a page, for the report steps: without the capture the release may land outside the board (see onPageRelease)
      if (!captureNoted && typeof board.hasPointerCapture === 'function' && !board.hasPointerCapture(e.pointerId)) {
        captureNoted = true;
        dragBack(`the board could not capture the pointer (${e.pointerType})`);
      }
    };
    let captureNoted = false;

    const onPointerMove = (e: PointerEvent) => {
      if (!press || e.pointerId !== press.pointerId) return;
      // a mouse that moves with no button held let go where the board did not hear it: before a drag the press ends as
      // cancelled after LOST_RELEASE_MOVES such moves, or its card would follow the pointer until the next click. A drag
      // ends only at its release (or the next press): Chrome on a Mac trackpad says no button now and then, mostly
      // right before the release, and ending the drag there sent the card back (report 1407). Not where the browser
      // says no buttons even at the press and during a drag (Safari 14): there every press would end at once.
      if (press.pointerType === 'mouse' && press.saysButtons && e.buttons === 0) {
        if (!press.dragging) {
          if (++press.noButtonMoves >= LOST_RELEASE_MOVES) {
            dragBack('press ended: moves with no button');
            finish(e, true);
          }
          return;
        }
      } else press.noButtonMoves = 0;
      const dx = e.clientX - press.startX;
      const dy = e.clientY - press.startY;
      if (!press.dragging) {
        if (Math.hypot(dx, dy) <= DRAG_THRESHOLD_PX) return;
        press.moved = true;
        if (!press.liftable) return;
        press.dragging = true;
        for (const el of press.runEls) el.setAttribute('data-dragging', '');
        // the run's first card casts the one shadow under the whole run (board.css, board/glide.ts)
        markRunHead(press.runEls);
      }
      // on the dragged cards only (owner 2026-10-17: the drag stuttered on a big Double Klondike waste): set
      // on the board, the inherited offset restyled every card on every move, ~100 face-up waste cards with
      // their pictures and trims among them
      for (const el of press.runEls) {
        el.style.setProperty('--drag-dx', `${dx}px`);
        el.style.setProperty('--drag-dy', `${dy}px`);
      }
    };

    const finish = (e: PointerEvent, cancelled: boolean) => {
      activePointers.delete(e.pointerId);
      if (!press || e.pointerId !== press.pointerId) return;
      const p = press;
      try {
        board.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      // at most half a card: on a phone the next Spider column is only ~35 px away, and a drop there must stay a drop
      const slop = Math.min(p.pointerType === 'mouse' ? CLICK_SLOP_PX : TAP_SLOP_PX, (p.cardEl?.offsetWidth ?? Infinity) / 2);
      const slipped = Math.hypot(e.clientX - p.startX, e.clientY - p.startY) <= slop;
      if (p.dragging) {
        if (!cancelled && p.pile && p.runEls[0]) {
          const from = { pile: p.pile, index: p.index };
          const dragged = p.runEls[0].getBoundingClientRect();
          const target = slipped ? null : pickDropTarget(dragged, collectCandidates(board, p.pileName, pairsGame()), (t) => ctrl.canDrop(from, t));
          clearDrag(!slipped, !!target);
          press = null;
          if (!slipped) {
            // A drag never counts as a click, and clicks right after it are ignored (JUST_DRAGGED_MS).
            lastClick = null;
            lastDragEndAt = Date.now();
            const result = target ? ctrl.moveRun(from, target, { snapped: motion().classic }) : ctrl.moveRun(from, { kind: 'stock' }); // a drop on nothing is a rejected move (starts nothing, legacy-compatible event)
            if (result === 'busy') dragBack('drag back: the game was busy');
            return;
          }
          // a short slip: the run is back at once and the press goes on as a click or a tap (below)
          dragBack(`short drag (under ${Math.round(slop)} px): taken as a click`);
        } else {
          dragBack(cancelled ? 'drag back: cancelled by the browser' : 'drag back: no card or pile');
          lastClick = null;
          lastDragEndAt = Date.now();
          clearDrag();
          press = null;
          return;
        }
      }
      press = null;
      if (cancelled || (p.moved && !slipped)) {
        // A press that moved further than CLICK_SLOP_PX is never a click, and it breaks a double-click
        // sequence; the next click within JUST_DRAGGED_MS is ignored too (legacy "just dragged" guard).
        lastClick = null;
        lastDragEndAt = Date.now();
        return;
      }
      if (Date.now() - lastDragEndAt < JUST_DRAGGED_MS) return;
      // A tap / click without a drag.
      if (pairsGame()) {
        pairClick(p);
        return;
      }
      if (p.pile?.kind === 'stock') {
        // A click on the EMPTY stock (a recycle) while the card the last click turned still flies to the
        // waste is a double tap on the last card, not a recycle (owner 2026-10-17: the last card's flight
        // was cut off and every card went back). Clicks on a stock with cards are never held (KL-B01).
        const recycle = ctrl.getState().piles.stock.length === 0;
        if (recycle && board.querySelector('.card[data-area="waste"][data-moving]')) return;
        ctrl.clickStock();
        return;
      }
      if (!p.cardEl || !p.pile) return;
      if (p.pointerType === 'touch') {
        // Mobile: a tap on the column's face-down top card flips it (auto-flip off); any other
        // single tap sends the card to a foundation (legacy behaviour).
        if (p.pile.kind === 'tableau' && p.cardEl.dataset.face === 'down') {
          if (p.index === ctrl.getState().piles.tableau[p.pile.index]!.length - 1) ctrl.flipTop(p.pile.index);
          return;
        }
        ctrl.sendToFoundation({ pile: p.pile, index: p.index });
        return;
      }
      // Mouse: a double-click (detected here, not from the dblclick event, which depends on the
      // browser's compatibility mouse events) sends the card to a foundation.
      const now = Date.now();
      if (lastClick && lastClick.cardEl === p.cardEl && now - lastClick.at <= DOUBLE_CLICK_MS) {
        lastClick = null;
        ctrl.sendToFoundation({ pile: p.pile, index: p.index });
        return;
      }
      lastClick = { cardEl: p.cardEl, at: now };
      // Only the column's TOP face-down card flips on a click (SPIDER-048); covered ones do nothing.
      if (p.pile.kind === 'tableau' && p.cardEl.dataset.face === 'down' && p.index === ctrl.getState().piles.tableau[p.pile.index]!.length - 1) ctrl.flipTop(p.pile.index);
    };

    const onPointerUp = (e: PointerEvent) => finish(e, false);
    const onPointerCancel = (e: PointerEvent) => finish(e, true);
    // The release heard by the page when the board did not hear it (report #cwkwmm0yle, 2026-10-27, Firefox 158 on a
    // Windows laptop with a touch screen, a mouse: every press of a Spider game lost its release, "it's frozen, I can't
    // move any card", although ENG-093 ended each lost press at the next one). The board takes the pointer's capture,
    // so the release comes to it; where the capture did not hold, it went to the element under the pointer and only
    // the page heard it.
    const onPageRelease = (e: PointerEvent) => {
      if (!press || e.pointerId !== press.pointerId) return;
      if (e.target instanceof Node && board.contains(e.target)) return; // the board's own listener had it
      dragBack(`release heard by the page, not the board (${e.type})`);
      finish(e, e.type === 'pointercancel');
    };
    // A mouse button let go with no pointerup at all: the pointerup comes before its mouseup, so a mouse press still
    // open at the mouseup lost it; the mouseup ends it, as the pointerup would have.
    const onPageMouseUp = (e: MouseEvent) => {
      if (!press || press.pointerType !== 'mouse' || e.button !== 0) return;
      dragBack('release from the mouseup (no pointerup came)');
      finish({ pointerId: press.pointerId, clientX: e.clientX, clientY: e.clientY } as PointerEvent, false);
    };

    const toFoundation = (e: MouseEvent) => {
      const cardEl = cardAt(e.target);
      const pile = cardEl ? parsePile(cardEl.dataset.pile) : null;
      if (!cardEl || !pile || pile.kind === 'stock') return;
      ctrl.sendToFoundation({ pile, index: Number(cardEl.dataset.index) });
    };
    const onDblClick = (e: MouseEvent) => e.preventDefault();
    // iPhone Safari takes a swipe that starts at the screen's left (or right) edge as Back / Forward, so
    // dragging a card of the first column left the page (owner 2026-10-23). A cancelled touchstart keeps
    // the gesture away; the board itself runs on pointer events only, which still fire.
    const onTouchStart = (e: TouchEvent) => {
      const x = e.touches[0]?.clientX;
      if (x !== undefined && (x < EDGE_SWIPE_PX || x > innerWidth - EDGE_SWIPE_PX)) e.preventDefault();
    };
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      toFoundation(e);
    };

    board.addEventListener('pointerdown', onPointerDown);
    board.addEventListener('pointermove', onPointerMove);
    board.addEventListener('pointerup', onPointerUp);
    board.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('pointerup', onPageRelease);
    window.addEventListener('pointercancel', onPageRelease);
    window.addEventListener('mouseup', onPageMouseUp);
    board.addEventListener('dblclick', onDblClick);
    board.addEventListener('contextmenu', onContextMenu);
    // on the whole board (#main), whose padding reaches the screen edge, not only on the cards' layer
    const table = board.closest<HTMLElement>('[data-board]') ?? board;
    table.addEventListener('touchstart', onTouchStart, { passive: false });
    return () => {
      board.removeEventListener('pointerdown', onPointerDown);
      board.removeEventListener('pointermove', onPointerMove);
      board.removeEventListener('pointerup', onPointerUp);
      board.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('pointerup', onPageRelease);
      window.removeEventListener('pointercancel', onPageRelease);
      window.removeEventListener('mouseup', onPageMouseUp);
      board.removeEventListener('dblclick', onDblClick);
      board.removeEventListener('contextmenu', onContextMenu);
      table.removeEventListener('touchstart', onTouchStart);
      unsubscribe();
      clearSelected();
    };
  }, [boardRef, ctrl]);
}
